import { Redirect, useLocalSearchParams } from 'expo-router';

import { appendLaunchParamsToHref, hrefPathname, useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

export default function Index() {
  const { currentRoute, isResolving } = useRulesEngine();
  // Forward the deep-link contract params on to the pre-account routes so the
  // (onboarding) layout can rehydrate from them. Without this, /?org=vida
  // and /?sso=foo lose their context as soon as this Redirect fires —
  // useGlobalSearchParams reads the new (empty) URL and the provider can't
  // restore selectedOrg / SSO mode. (`vida_client` / `vida_client_version` are
  // re-appended to every navigation by appendLaunchParamsToHref, so they don't
  // need forwarding here.)
  const { org, sso, token } = useLocalSearchParams<{
    org?: string;
    sso?: string;
    token?: string;
  }>();

  // Wait for the rules engine to resolve the destination, not just for auth to
  // settle. authStatus flips to authenticated the moment the user fetch lands,
  // but currentRoute is still null until the driver finishes resolving (it may
  // have resource fetches in flight). Redirecting on authStatus alone sent a
  // resuming user to /step/account-creation before the deeper route resolved,
  // flashing that screen until the layout corrected it. isResolving covers both.
  if (isResolving || !currentRoute) {
    return <LoadingScreen />;
  }

  // Redirect straight to the resolved route. The resolver decides WHICH route
  // (including diverting a valid PURL deep link to /step/purl-dob-verification —
  // the token is read here at `/`, so that decision is made before we navigate);
  // index only forwards the deep-link params, since those two routes are the ones
  // the resolver emits as bare paths. Every other resolved route carries its own
  // params or needs none. The launch params (`vida_client` / …) are layered on by
  // appendLaunchParamsToHref so they persist across the flow.
  const path = hrefPathname(currentRoute);

  // PURL: forward token + org so the provider can re-validate on refresh and the
  // entry screen can title itself. (Reached only when the resolver picked the
  // entry route — i.e. a valid token on a no-account user.)
  if (path === '/step/purl-dob-verification' && token && org) {
    return (
      <Redirect
        href={appendLaunchParamsToHref({
          pathname: '/step/purl-dob-verification',
          params: { token, org },
        })}
      />
    );
  }

  if (path === '/step/account-creation' && (org || sso)) {
    const params: { org?: string; sso?: string } = {};
    if (org) params.org = org;
    if (sso) params.sso = sso;
    return (
      <Redirect href={appendLaunchParamsToHref({ pathname: '/step/account-creation', params })} />
    );
  }

  return <Redirect href={appendLaunchParamsToHref(currentRoute)} />;
}
